import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { Search, Loader2, Clock, User, LogIn, X, MessageCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useAuth } from "@/contexts/AuthContext";
import { usePushNotifications } from "@/hooks/usePushNotifications";
import AuthModal from "@/components/AuthModal";
import NotificationBell from "@/components/NotificationBell";
import ArticleModal from "@/components/ArticleModal";
import HeroCarousel from "@/components/news/HeroCarousel";
import TrendingSection from "@/components/news/TrendingSection";
import NewsCard from "@/components/news/NewsCard";
import { useNavigate } from "react-router-dom";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CATEGORIES = [
  { id: "all", label: "All News" },
  { id: "us-politics", label: "US Politics" },
  { id: "general", label: "General" },
  { id: "business", label: "Business" },
  { id: "technology", label: "Technology" },
  { id: "sports", label: "Sports" },
  { id: "entertainment", label: "Entertainment" },
  { id: "health", label: "Health" },
  { id: "science", label: "Science" },
  { id: "us-weather", label: "US Weather" },
];

const COUNTRIES = [
  { code: "all", name: "All Countries" },
  { code: "us", name: "United States" },
  { code: "gb", name: "United Kingdom" },
  { code: "ca", name: "Canada" },
  { code: "au", name: "Australia" },
  { code: "qa", name: "Qatar" },
  { code: "za", name: "South Africa" },
  { code: "ng", name: "Nigeria" },
  { code: "hk", name: "Hong Kong" },
  { code: "jp", name: "Japan" },
  { code: "in", name: "India" },
  { code: "sg", name: "Singapore" },
];

const NewsPage = () => {
  const { user, getAuthHeader } = useAuth();
  const navigate = useNavigate();
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState("all");
  const [selectedCountry, setSelectedCountry] = useState("all");
  const [searchQuery, setSearchQuery] = useState("");
  const [searchInput, setSearchInput] = useState("");
  const [totalResults, setTotalResults] = useState(0);
  const [sources, setSources] = useState([]);
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [selectedArticle, setSelectedArticle] = useState(null);
  const [showArticleModal, setShowArticleModal] = useState(false);
  const [showWelcome, setShowWelcome] = useState(false);
  const [showPushBanner, setShowPushBanner] = useState(false);
  const [trending, setTrending] = useState([]);
  const [bookmarkedUrls, setBookmarkedUrls] = useState(new Set());
  const { isSupported: pushSupported, isSubscribed: pushSubscribed, subscribe: subscribePush } = usePushNotifications();
  const [currentTime, setCurrentTime] = useState(new Date());
  const [userTimezone, setUserTimezone] = useState("");

  useEffect(() => {
    setUserTimezone(Intl.DateTimeFormat().resolvedOptions().timeZone);
    const timer = setInterval(() => setCurrentTime(new Date()), 1000);
    return () => clearInterval(timer);
  }, []);

  useEffect(() => {
    const dismissed = sessionStorage.getItem("gnh_welcome_dismissed");
    if (!dismissed) {
      const timer = setTimeout(() => setShowWelcome(true), 2000);
      return () => clearTimeout(timer);
    }
  }, []);

  useEffect(() => {
    if (pushSupported && !pushSubscribed) {
      const pushDismissed = localStorage.getItem("gnh_push_dismissed");
      if (!pushDismissed) {
        const timer = setTimeout(() => setShowPushBanner(true), 8000);
        return () => clearTimeout(timer);
      }
    }
  }, [pushSupported, pushSubscribed]);

  const handleEnablePush = async () => {
    await subscribePush();
    setShowPushBanner(false);
  };

  const dismissPushBanner = () => {
    setShowPushBanner(false);
    localStorage.setItem("gnh_push_dismissed", "1");
  };

  const dismissWelcome = () => {
    setShowWelcome(false);
    sessionStorage.setItem("gnh_welcome_dismissed", "1");
  };

  const getFallbackImage = (category) => {
    const categoryFallbacks = {
      "us-politics": "https://images.unsplash.com/photo-1529107386315-e1a2ed48a620?q=80&w=2070&auto=format&fit=crop",
      "general": "https://images.unsplash.com/photo-1504711434969-e33886168f5c?q=80&w=2070&auto=format&fit=crop",
      "business": "https://images.unsplash.com/photo-1454165804606-c3d57bc86b40?q=80&w=2070&auto=format&fit=crop",
      "technology": "https://images.unsplash.com/photo-1518770660439-4636190af475?q=80&w=2070&auto=format&fit=crop",
      "sports": "https://images.unsplash.com/photo-1461896836934-ffe607ba8211?q=80&w=2070&auto=format&fit=crop",
      "entertainment": "https://images.unsplash.com/photo-1574267432644-f610f5ae4401?q=80&w=2070&auto=format&fit=crop",
      "health": "https://images.unsplash.com/photo-1505751172876-fa1923c5c528?q=80&w=2070&auto=format&fit=crop",
      "science": "https://images.unsplash.com/photo-1532094349884-543bc11b234d?q=80&w=2070&auto=format&fit=crop",
      "us-weather": "https://images.unsplash.com/photo-1534088568595-a066f410bcda?q=80&w=2070&auto=format&fit=crop",
    };
    return categoryFallbacks[category] || categoryFallbacks["general"];
  };

  const fetchNews = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (selectedCategory !== "all") params.append("category", selectedCategory);
      if (selectedCountry !== "all") params.append("country", selectedCountry);
      if (searchQuery) params.append("search", searchQuery);
      const response = await axios.get(`${API}/news?${params.toString()}`);
      setArticles(response.data.articles);
      setTotalResults(response.data.total_results);
      setSources(response.data.sources);
    } catch (error) {
      // News fetch error handled silently
    } finally {
      setLoading(false);
    }
  }, [selectedCategory, selectedCountry, searchQuery]);

  useEffect(() => {
    fetchNews();
  }, [fetchNews]);

  useEffect(() => {
    const fetchTrending = async () => {
      try {
        const { data } = await axios.get(`${API}/trending?limit=6`);
        setTrending(data.trending);
      } catch (err) { console.error("Failed to fetch trending:", err); }
    };
    fetchTrending();
  }, []);

  useEffect(() => {
    if (user && user !== false) {
      const fetchBookmarks = async () => {
        try {
          const { data } = await axios.get(`${API}/bookmarks`, { headers: getAuthHeader() });
          setBookmarkedUrls(new Set(data.bookmarks.map(b => b.article_url)));
        } catch (err) { console.error("Failed to fetch bookmarks:", err); }
      };
      fetchBookmarks();
    }
  }, [user]);

  const toggleBookmark = async (article, e) => {
    e.stopPropagation();
    e.preventDefault();
    if (!user || user === false) { setShowAuthModal(true); return; }
    const isBookmarked = bookmarkedUrls.has(article.url);
    try {
      if (isBookmarked) {
        await axios.delete(`${API}/bookmarks?article_url=${encodeURIComponent(article.url)}`, { headers: getAuthHeader() });
        setBookmarkedUrls(prev => { const n = new Set(prev); n.delete(article.url); return n; });
      } else {
        await axios.post(`${API}/bookmarks`, {
          article_url: article.url,
          article_title: article.title,
          article_source: article.source,
          article_image: article.image_url,
          article_category: article.category,
          article_published_at: article.published_at
        }, { headers: getAuthHeader() });
        setBookmarkedUrls(prev => new Set([...prev, article.url]));
      }
    } catch (err) { console.error("Failed to toggle bookmark:", err); }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    setSearchQuery(searchInput);
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diff = Math.floor((now - date) / 1000);
    const relativeTime = diff < 3600 ? `${Math.floor(diff / 60)}m ago` :
                         diff < 86400 ? `${Math.floor(diff / 3600)}h ago` :
                         diff < 604800 ? `${Math.floor(diff / 86400)}d ago` :
                         date.toLocaleDateString("en-US", { month: "short", day: "numeric" });
    const fullDate = date.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
    return { relativeTime, fullDate, timestamp: date.toLocaleTimeString("en-US", { hour: "2-digit", minute: "2-digit" }) };
  };

  const handleArticleClick = (article, e) => {
    e.preventDefault();
    setSelectedArticle(article);
    setShowArticleModal(true);
  };

  return (
    <div className="min-h-screen" style={{ backgroundColor: "var(--color-bg)" }}>
      {/* Header */}
      <header className="sticky top-0 z-40" style={{ backgroundColor: "var(--color-bg)", borderBottom: "2px solid var(--color-border)" }}>
        <div className="max-w-[1600px] mx-auto px-3 sm:px-6 lg:px-8 py-3 sm:py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2 sm:gap-3">
              <a href="https://www.globalnewshub.us" target="_blank" rel="noopener noreferrer">
                <img src="/gnh-logo.png" alt="Global News Hub" className="h-10 sm:h-12 w-auto rounded-xl" />
              </a>
              <h1 className="text-xl sm:text-3xl font-black tracking-tighter" style={{ color: "var(--color-text)", fontFamily: "Cabinet Grotesk, sans-serif" }} data-testid="site-logo">
                GLOBAL NEWS <span style={{ color: "var(--color-accent)" }}>HUB</span>
              </h1>
            </div>

            {/* Clock - only visible on xl+ screens inline */}
            <div className="hidden xl:flex items-center gap-2" data-testid="time-widget">
              <Clock className="w-3 h-3" style={{ color: "var(--color-text-dim)" }} />
              <span className="text-[11px] font-semibold" style={{ color: "var(--color-text-muted)" }} data-testid="current-time">
                {currentTime.toLocaleTimeString("en-US", { hour: "2-digit", minute: "2-digit", second: "2-digit", hour12: true })}
              </span>
              <span className="text-[10px]" style={{ color: "var(--color-text-dim)" }}>•</span>
              <span className="text-[10px]" style={{ color: "var(--color-text-dim)" }} data-testid="current-date">
                {currentTime.toLocaleDateString("en-US", { weekday: "long", month: "long", day: "numeric", year: "numeric" })}
              </span>
              <span className="text-[10px]" style={{ color: "var(--color-text-dim)" }}>•</span>
              <span className="text-[10px]" style={{ color: "var(--color-text-dim)" }} data-testid="region-info">{userTimezone}</span>
            </div>

            <div className="flex items-center gap-2 sm:gap-3">
              <div className="flex items-center gap-1.5 sm:gap-2 px-2 sm:px-3 py-1 rounded-full" style={{ backgroundColor: "var(--color-live)" }}>
                <div className="w-2 h-2 bg-white rounded-full animate-pulse"></div>
                <span className="text-[10px] sm:text-xs font-bold tracking-wider uppercase text-white" data-testid="live-indicator">LIVE</span>
              </div>
              {user && user !== false ? (
                <>
                  <NotificationBell />
                  <button
                    onClick={() => navigate("/profile")}
                    className="flex items-center gap-2 px-3 sm:px-4 py-2 rounded-full transition-colors"
                    style={{ backgroundColor: "var(--color-card)", color: "var(--color-text)" }}
                    data-testid="user-profile-button"
                  >
                    <User className="w-4 h-4" />
                    <span className="text-sm font-medium hidden sm:inline">@{user.user_tag || user.name}</span>
                  </button>
                </>
              ) : (
                <Button
                  onClick={() => setShowAuthModal(true)}
                  className="rounded-full px-4 sm:px-6 flex items-center gap-2 text-white text-xs sm:text-sm"
                  style={{ backgroundColor: "var(--color-accent)" }}
                  data-testid="login-button"
                >
                  <LogIn className="w-4 h-4" />
                  <span className="hidden sm:inline">Sign In</span>
                </Button>
              )}
            </div>
          </div>

          {/* Clock bar - visible on tablet and mobile below the header row */}
          <div className="flex xl:hidden items-center justify-center gap-2 mt-2 py-1.5 rounded-lg" style={{ backgroundColor: "var(--color-card)" }} data-testid="time-widget-mobile">
            <Clock className="w-3 h-3" style={{ color: "var(--color-text-dim)" }} />
            <span className="text-[10px] sm:text-[11px] font-semibold" style={{ color: "var(--color-text-muted)" }}>
              {currentTime.toLocaleTimeString("en-US", { hour: "2-digit", minute: "2-digit", second: "2-digit", hour12: true })}
            </span>
            <span className="text-[10px]" style={{ color: "var(--color-text-dim)" }}>•</span>
            <span className="text-[10px] sm:text-[11px]" style={{ color: "var(--color-text-dim)" }}>
              {currentTime.toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric", year: "numeric" })}
            </span>
            <span className="text-[10px]" style={{ color: "var(--color-text-dim)" }}>•</span>
            <span className="text-[10px]" style={{ color: "var(--color-text-dim)" }}>{userTimezone}</span>
          </div>
        </div>

        {/* Category Navigation */}
        <div className="overflow-x-auto hide-scrollbar" style={{ borderTop: "1px solid var(--color-border)" }}>
          <style>{`.hide-scrollbar::-webkit-scrollbar { display: none; } .hide-scrollbar { -ms-overflow-style: none; scrollbar-width: none; }`}</style>
          <div className="max-w-[1600px] mx-auto px-3 sm:px-6 lg:px-8">
            <div className="flex gap-1.5 sm:gap-2 py-2 sm:py-3">
              {CATEGORIES.map((cat) => (
                <button
                  key={cat.id}
                  onClick={() => setSelectedCategory(cat.id)}
                  data-testid={`category-${cat.id}`}
                  className="px-3 sm:px-5 py-1.5 sm:py-2 text-xs sm:text-sm font-bold tracking-wider uppercase whitespace-nowrap rounded-full transition-all"
                  style={
                    selectedCategory === cat.id
                      ? { backgroundColor: "var(--color-accent)", color: "#fff", boxShadow: "0 4px 12px var(--color-accent-glow)" }
                      : { backgroundColor: "var(--color-card)", color: "var(--color-text-muted)" }
                  }
                >
                  {cat.label}
                </button>
              ))}
            </div>
          </div>
        </div>
      </header>

      {/* Filters */}
      <div style={{ backgroundColor: "var(--color-card)", borderBottom: "1px solid var(--color-border)" }}>
        <div className="max-w-[1600px] mx-auto px-3 sm:px-6 lg:px-8 py-4 sm:py-6">
          <div className="flex flex-col sm:flex-row gap-3 sm:gap-4">
            <form onSubmit={handleSearch} className="flex-1" data-testid="search-form">
              <div className="flex gap-2">
                <Input
                  type="text"
                  placeholder="Search news..."
                  value={searchInput}
                  onChange={(e) => setSearchInput(e.target.value)}
                  data-testid="search-input"
                  className="flex-1 rounded-lg text-sm"
                  style={{ backgroundColor: "var(--color-input-bg)", borderColor: "var(--color-border)", color: "var(--color-text)" }}
                />
                <Button type="submit" data-testid="search-button" className="rounded-lg px-4 sm:px-6 text-white" style={{ backgroundColor: "var(--color-accent)" }}>
                  <Search className="w-4 h-4" />
                </Button>
              </div>
            </form>
            <Select value={selectedCountry} onValueChange={setSelectedCountry}>
              <SelectTrigger className="w-full sm:w-[200px] rounded-lg" style={{ backgroundColor: "var(--color-input-bg)", borderColor: "var(--color-border)", color: "var(--color-text)" }} data-testid="country-filter">
                <SelectValue placeholder="Country" />
              </SelectTrigger>
              <SelectContent style={{ backgroundColor: "var(--color-card)", borderColor: "var(--color-border)" }}>
                {COUNTRIES.map((country) => (
                  <SelectItem key={country.code} value={country.code} data-testid={`country-${country.code}`} style={{ color: "var(--color-text)" }}>
                    {country.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="mt-3 sm:mt-4 flex items-center gap-3 sm:gap-4 text-xs" style={{ color: "var(--color-text-dim)" }}>
            <span data-testid="total-results">{totalResults} articles</span>
            <span>•</span>
            <span data-testid="sources-count">{sources.length} sources</span>
            <span className="hidden sm:inline">•</span>
            <span className="hidden sm:inline" style={{ color: "var(--color-live)" }}>Auto-updates every 2 min</span>
          </div>
        </div>
      </div>

      {/* Push Notification Banner */}
      {showPushBanner && !showWelcome && (
        <div className="max-w-[1600px] mx-auto px-3 sm:px-6 lg:px-8 mt-3 sm:mt-4">
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 p-3 sm:p-4 rounded-xl" data-testid="push-banner"
            style={{ backgroundColor: "var(--color-accent-glow)", border: "1px solid var(--color-accent)" }}>
            <div className="flex items-center gap-3">
              <span className="text-lg sm:text-xl">🔔</span>
              <p className="text-xs sm:text-sm font-medium" style={{ color: "var(--color-text)" }}>
                Get notified instantly when breaking news happens!
              </p>
            </div>
            <div className="flex items-center gap-2 w-full sm:w-auto">
              <Button onClick={handleEnablePush} size="sm" className="text-white text-xs px-4 flex-1 sm:flex-none"
                style={{ backgroundColor: "var(--color-accent)" }} data-testid="enable-push-button">
                Enable Notifications
              </Button>
              <button onClick={dismissPushBanner} className="p-1" style={{ color: "var(--color-text-dim)" }}>
                <X className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Main Content */}
      <main className="max-w-[1600px] mx-auto px-3 sm:px-6 lg:px-8 py-4 sm:py-8">
        {loading ? (
          <div className="flex items-center justify-center py-20" data-testid="loading-indicator">
            <Loader2 className="w-8 h-8 animate-spin" style={{ color: "var(--color-accent)" }} />
          </div>
        ) : articles.length === 0 ? (
          <div className="text-center py-20" data-testid="no-results">
            <p className="text-xl" style={{ color: "var(--color-text-muted)" }}>No articles found</p>
          </div>
        ) : (
          <>
            <HeroCarousel
              articles={articles}
              formatDate={formatDate}
              onArticleClick={handleArticleClick}
              getFallbackImage={getFallbackImage}
            />

            <TrendingSection
              trending={trending}
              articles={articles}
              formatDate={formatDate}
              onArticleClick={handleArticleClick}
            />

            {/* News Grid */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6" data-testid="news-grid">
              {articles.slice(4).map((article, idx) => (
                <NewsCard
                  key={article.url || idx}
                  article={article}
                  idx={idx}
                  formatDate={formatDate}
                  onArticleClick={handleArticleClick}
                  onToggleBookmark={toggleBookmark}
                  isBookmarked={bookmarkedUrls.has(article.url)}
                  getFallbackImage={getFallbackImage}
                  onAuthClick={() => setShowAuthModal(true)}
                />
              ))}
            </div>
          </>
        )}
      </main>

      {/* Footer */}
      <footer className="mt-8 sm:mt-16" style={{ backgroundColor: "var(--color-bg)", borderTop: "2px solid var(--color-border)" }}>
        <div className="max-w-[1600px] mx-auto px-3 sm:px-6 lg:px-8 py-6 sm:py-8">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4 mb-4">
            <div className="text-center sm:text-left flex-1">
              <p className="text-xs sm:text-sm" style={{ color: "var(--color-text-dim)" }}>
                News aggregated from {sources.length} international sources
              </p>
              <p className="text-[10px] sm:text-xs mt-1 sm:mt-2" style={{ color: "var(--color-text-dim)" }}>
                © 2026 Global News Hub • All articles belong to their respective publishers
              </p>
            </div>
            <div className="flex items-center gap-2 sm:gap-3">
              <button
                onClick={() => navigate("/about")}
                className="px-3 sm:px-4 py-2 rounded-lg font-semibold transition-colors text-xs sm:text-sm"
                style={{ backgroundColor: "var(--color-card)", color: "var(--color-text)", border: "1px solid var(--color-border)" }}
                data-testid="footer-about-link"
              >
                About Us
              </button>
              <button
                onClick={() => navigate("/admin")}
                className="px-3 sm:px-4 py-2 rounded-lg font-semibold transition-colors flex items-center gap-2 text-white text-xs sm:text-sm"
                style={{ backgroundColor: "var(--color-accent)" }}
                data-testid="footer-admin-link"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                </svg>
                Admin
              </button>
            </div>
          </div>
        </div>
      </footer>

      <AuthModal isOpen={showAuthModal} onClose={() => setShowAuthModal(false)} />
      <ArticleModal
        article={selectedArticle}
        isOpen={showArticleModal}
        onClose={() => { setShowArticleModal(false); setSelectedArticle(null); }}
        onAuthClick={() => { setShowArticleModal(false); setShowAuthModal(true); }}
      />

      {/* Welcome Popup */}
      {showWelcome && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm" data-testid="welcome-popup-overlay">
          <div
            className="relative w-full max-w-md mx-4 rounded-2xl shadow-2xl overflow-hidden"
            style={{ backgroundColor: "var(--color-card)", border: "1px solid var(--color-border)", boxShadow: "0 8px 40px var(--color-accent-glow)" }}
            data-testid="welcome-popup"
          >
            <button
              onClick={dismissWelcome}
              className="absolute top-4 right-4 p-1 rounded-full transition-colors z-10"
              style={{ color: "var(--color-text-dim)" }}
              data-testid="welcome-popup-close"
            >
              <X className="w-5 h-5" />
            </button>

            <div className="p-8 text-center">
              <div className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-5"
                style={{ backgroundColor: "var(--color-accent-glow)", border: "2px solid var(--color-accent)" }}>
                <MessageCircle className="w-8 h-8" style={{ color: "var(--color-accent)" }} />
              </div>

              <h3 className="text-2xl font-black mb-3" style={{ color: "var(--color-text)", fontFamily: "Cabinet Grotesk, sans-serif" }}>
                Join the Conversation!
              </h3>

              <p className="text-base leading-relaxed mb-6" style={{ color: "var(--color-text-muted)" }}>
                Have thoughts on the issues of the day? You can leave comments, reply to other readers, and like the takes you agree with. Sign up and make your voice heard!
              </p>

              <div className="flex flex-col gap-3">
                <Button
                  onClick={() => { dismissWelcome(); setShowAuthModal(true); }}
                  className="w-full rounded-full py-3 font-bold text-white text-base"
                  style={{ backgroundColor: "var(--color-accent)" }}
                  data-testid="welcome-signup-button"
                >
                  Sign Up to Comment
                </Button>
                <button
                  onClick={dismissWelcome}
                  className="text-sm font-medium py-2 transition-colors"
                  style={{ color: "var(--color-text-dim)" }}
                  data-testid="welcome-dismiss-button"
                >
                  Maybe later
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default NewsPage;
